
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import Image from 'next/image';
import disclosureRendering from '../../../public/images/disclosures-rendering-example.png';
export const meta = {
  title: 'Home Improvement & Roofing Workflow Code Samples',
  category: 'Guides',
  published: true,
  sort: 8
};

const layoutProps = {
  meta
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`Home Improvement & Roofing Workflow Code Samples`}</h1>
    <p><strong parentName="p">{`Congratulations, you’re a GoodLeap Partner!`}</strong></p>
    <p>{`You are now able to build a custom user experience using the GoodLeap Developer API. Outlined below are the steps to create and manage your organization’s loans using the various resources available.`}</p>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/docs/workflow-diagrams"
      }}>{`Workflow Diagrams`}</a>{` for the sequence diagram representation of this process for context before getting into the code samples. These workflow examples are not meant to be an exhaustive list and the APIs can be used outside of these workflows, but the workflows do provide a common starting place and template for integrating with our APIs. For example, you may want to provide a preview of what the loan payment will be during the presentation of the offers by using our `}<inlineCode parentName="p">{`GET /v2/payments`}</inlineCode>{` loan calculator which is not represented in the examples below, but can be easily achieved.`}</p>
    <blockquote>
      <p parentName="blockquote"><em parentName="p">{`note: while Home Improvement & Roofing loans are similar in workflow to Solar & Battery Loans there are noteable differences you should be aware of that will be covered here`}</em></p>
    </blockquote>
    <h2>{`User Management`}</h2>
    <p>{`For almost all critical Home Improvement and Roofing actions, such as loan submission and sharing a loan application, we will ensure that the submitting user exists within your organization. If you already have users who have used the Origin platform in your organization you can use their associated userName instead of creating a new user for them.`}</p>
    <h3>{`1. Creating a New User in Your Organization`}</h3>
    <p>{`A new user can be created by using `}<inlineCode parentName="p">{`POST /v2/users`}</inlineCode>{` and passing in:`}</p>
    <ul>
      <li parentName="ul">{`First Name`}</li>
      <li parentName="ul">{`Last Name`}</li>
      <li parentName="ul">{`Mobile Phone`}</li>
      <li parentName="ul">{`Role ID`}</li>
      <li parentName="ul">{`Email`}</li>
      <li parentName="ul">{`Password`}</li>
    </ul>
    <p>{`Here is a sample `}<inlineCode parentName="p">{`POST /v2/users`}</inlineCode>{` request`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "firstName": "string",
  "lastName": "string",
  "password": "string",
  "email": "string",
  "mobilePhone": "string",
  "roleId": "string"
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`For more information, please see `}<a parentName="p" {...{
          "href": "/reference#tag/Users"
        }}>{`Users`}</a></p>
    </blockquote>
    <p>{`The role ID can be obtained by calling `}<inlineCode parentName="p">{`GET /v2/users/roles-channels`}</inlineCode>{` and selecting the desired role ID you'd like the new user to have.`}</p>
    <h3>{`2. Working with Existing Users`}</h3>
    <p>{`We have provided several endpoints to help you work with existing users within your organization.`}</p>
    <p><inlineCode parentName="p">{`GET /v2/users`}</inlineCode>{` will return a list of all user in your organization in a paginated list.
here is an example response from `}<inlineCode parentName="p">{`GET /v2/users`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "id": "string",
  "firstName": "string",
  "lastName": "string",
  "email": "string",
  "phoneNumber": {
    "type": "MOBILE",
    "number": "string"
  },
  "enabled": true,
  "license": {
    "id": "string",
    "status": "ACTIVE"
  }
}
`}</code></pre>
    <p>{`If you wish to drill down into the specifics of an individual user you can use `}<inlineCode parentName="p">{`GET /v2/user/:id`}</inlineCode>{` which will allow you to get more information on a user by passing in an email address that can be found by using `}<inlineCode parentName="p">{`GET /v2/users`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "id": "string",
  "firstName": "string",
  "lastName": "string",
  "email": "string",
  "phoneNumber": "string",
  "enabled": true,
  "license": {
    "id": "string",
    "status": "ACTIVE"
  },
  "phones": [
    {
      "type": "MOBILE",
      "number": "string"
    }
  ],
  "deleted": true,
  "role": {
    "id": "string",
    "name": "string"
  },
  "channels": [
    {
      "code": "string",
      "name": "string"
    }
  ]
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`for more information, see `}<a parentName="p" {...{
          "href": "/reference#operation/getPosfinancingRestV2UserUserid"
        }}>{`GET user by Id`}</a>{` in the API documentation.`}</p>
    </blockquote>
    <h3>{`3. Updating Users information`}</h3>
    <p>{`When Updating a user you have 4 endpoints that will help you update specific pieces of data related to a user.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><inlineCode parentName="p">{`PUT /v2/users/:id/role`}</inlineCode>{`
This allows you to update a users role(s)`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><inlineCode parentName="p">{`PUT /v2/users/:id/channels`}</inlineCode>{`
This allows you to update a users channel(s)`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><inlineCode parentName="p">{`PUT /v2/users/:id/license`}</inlineCode>{`
This allows you to update a users sales license`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><inlineCode parentName="p">{`PUT /v2/users/:id`}</inlineCode>{`
This allows you to update a users information such as 'mobilePhone'`}</p>
      </li>
    </ol>
    <p>{`Examples of these can be found in `}<a parentName="p" {...{
        "href": "/reference#tag/Users"
      }}>{`Users`}</a>{` section of the API documentation`}</p>
    <h2>{`Submitting a Loan Application`}</h2>
    <h3>{`1. Present Loan Offers`}</h3>
    <p>{`Once you create an application page, you are now ready to present loan offers to your borrower(s).`}</p>
    <p><strong parentName="p">{`loan offers`}</strong>{` contain the rate & term, along with the category, product, and any promotions, that can be displayed to your borrower(s).`}</p>
    <p>{`The first step in retrieving loan offers is to check what promotions are available to your organization. `}<strong parentName="p">{`Promotions`}</strong>{` are offerings that affect the borrower’s monthly payments (ex. discounts, down payments, payments paid by X, etc.). Make a `}<inlineCode parentName="p">{`GET`}</inlineCode>{` request to `}<inlineCode parentName="p">{`/v2/promotions`}</inlineCode>{` to for a list of available promotions. For more information, see `}<a parentName="p" {...{
        "href": "/docs/showcase-promotions"
      }}>{`Showcasing Promotions`}</a>{`.`}</p>
    <p>{`Now that you’ve checked your promotions, you’re ready to see your loan offers. `}<em parentName="p">{`But wait!`}</em>{` The list of available loan offers could potentially be lengthy, so we allow you to filter the results by `}<strong parentName="p">{`channel`}</strong>{` (groups of offers defined for your organization) or `}<strong parentName="p">{`state`}</strong>{` (the geographic territories in which your organization can operate). You can make `}<inlineCode parentName="p">{`GET`}</inlineCode>{` requests to the `}<inlineCode parentName="p">{`/v2/channels`}</inlineCode>{` and `}<inlineCode parentName="p">{`/v2/states`}</inlineCode>{` resources to get your organization’s channels & states. You can now filter your loan offers by either of these dimensions.`}</p>
    <p>{`Here is an example of what you would receive when calling loan offers (`}<inlineCode parentName="p">{`GET /v2/offers`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "data": [
        {
            "id": "ee7ea1c7-5bad-422c-b567-ec40e1170e3d",
            "name": "Standard installment - 10 year - 2.99% with Autopay",
            "rate": 2.99,
            "term": 10,
            "categoryId": "07f44183-4f79-465f-b3e4-e50f5c122bd7",
            "productCode": "STANDARD_SOLAR",
            "promotionIds": [
                "a92d13dd-5c2c-45ea-a178-3206994fdeee"
            ],
            "channels": [
                "apply",
                "apply1",
                "Admin"
            ],
            "states": [
                "AK",
                "AL",
                "AZ",
                "CA",
                "GA",
                "IL",
                "KY",
                "MS",
                "MT",
                "NV",
                "NY",
                "OK",
                "PR",
                "RI",
                "SC",
                "TN",
                "TX",
                "WI"
            ],
            "dealerFee": 17.25
        },
        {
          <additional offers sections>
        }
    ]
}
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/getRestV2Offers"
      }}>{`Get Offers`}</a>{` in the API documentation.`}</p>
    <h3>{`2. Capture Disclosures`}</h3>
    <p>{`The next thing is to capture disclosures. The disclosures must be presented and agreed to by the borrower(s) before a loan application can be submitted. A historical list of disclosures is available from the `}<inlineCode parentName="p">{`GET /v2/disclosures`}</inlineCode>{` resource but only the current disclosures will contain the disclosures token (can be reused many times but expires after `}<inlineCode parentName="p">{`tokenExpiresAt`}</inlineCode>{` indicated in the response) required for submitting a loan application with the `}<inlineCode parentName="p">{`POST /v2/loans`}</inlineCode>{` call. To have the disclosures resource only return the current disclosures use `}<inlineCode parentName="p">{`GET /v2/disclosures?current=true`}</inlineCode>{`. The disclosures contain different sections with indicators as to whether the text is suggested as bold, if there should be an accompanying checkbox for the applicant to indicate consent, as well as whether there should be a signatureBox next to the section to make it easy to render your loan application page based on the data in the disclosures.`}</p>
    <p>{`Here’s an example of what to expect in the disclosures response:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": [{
    "id": "329eff2a-c2af-45de-8b0b-73dfac8efa74",
    "tokenExpiresAt": "2023-08-12T20:17:46.384Z",
    "name": "Example Disclosure V2",
    "current": true,
    "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c",
    "sections": [{
        "boldText": true,
        "checkbox": false,
        "signatureBox": false,
        "sort": 0,
        "text": "GoodLeap may require additional documents to verify the above information. If it is determined the information is inaccurate, GoodLeap reserves the right to cancel an accepted loan even if loan docs have been signed."
      },
      {
        "boldText": false,
        "checkbox": false,
        "signatureBox": false,
        "sort": 1,
        "text": "1. You have read and agree to our [Consent to Conduct Business Electronically (E-Sign Consent)](https://goodleap.com/esign-consent) disclosures, so that GoodLeap may communicate with you electronically. In addition, you agree to allow any of our assignees to communicate with you electronically as well for all monthly statements or year-end tax documentation. \\n\\t- 2. You agree the information provided above is accurate and you authorize GoodLeap to share your personal information with our underwriting provider and others that we may sell and transfer your accepted loan to. \\n\\t- 3. You authorize GoodLeap to obtain a consumer credit report on you and other information to authenticate your identity and make a credit decision. You also authorize GoodLeap to share with your solar solution provider the underwriting decisions we make and have read and understand our [Privacy Policy](https://goodleap.com/privacy) \\n\\t- 4. You are providing express written consent and agree that GoodLeap may contact you, including by text, at the email or phone number(s) you have provided and you have read and agree to the [Express Written Consent](https://goodleap.com/written-consent) disclosure outlining our communication practices. \\n\\t- 5. I have reviewed the state specific disclosures. \\n\\t- You can revoke this authorization and may withdraw your consent to receive electronic documents at any time by sending a withdraw request to GoodLeap at 8781 Sierra College Blvd. Roseville, CA, 95661 or at withdrawconsent@goodleap.com \\n\\t- 2. * Alimony, child support, or separate maintenance income does not need to be disclosed if you do not wish to have it considered as a basis for repaying this obligation"
      },
      {
        "boldText": false,
        "checkbox": true,
        "signatureBox": false,
        "sort": 2,
        "text": "By checking this box, you are agreeing to the E-Sign Consent in item 1 above."
      },
      {
        "boldText": false,
        "checkbox": true,
        "signatureBox": false,
        "sort": 3,
        "text": "By checking this box, all borrowers individually authorize GoodLeap to generate a Soft Credit Report and each borrower individually provides consent and agrees to each of the following statements above. This will not affect your credit score in any way until you accept the prequalified loan offer and we fund your loan. If two borrowers have provided information above, both understand and give express intent to apply jointly."
      },
      {
        "boldText": false,
        "checkbox": false,
        "signatureBox": true,
        "sort": 4,
        "text": "Borrower, by signing below you acknowledge that all information provided on this form is true and correct to the best of your knowledge, and you authorize, consent to and agree with each of the items 1 through 5 above."
      },
      {
        "boldText": false,
        "checkbox": false,
        "signatureBox": false,
        "sort": 5,
        "text": "State Specific Disclosures \\n\\t- CA RESIDENTS:A married applicant may apply for a separate account. \\n\\t- NJ RESIDENTS:The section headings of this Application are a table of contents and not contract terms. Portions of this Application with references to actions taken to the extent of applicable law apply to acts or practices that New Jersey law permits or requires. In this Application, acts or practices (i) by you which are or may be permitted by “applicable law” are permitted by New Jersey law, and (ii) that may or will be taken by you unless prohibited by “applicable law” are permitted by New Jersey law. \\n\\t- NY and RI RESIDENTS:A consumer report (credit report) may be obtained from a consumer-reporting agency (credit bureau) in connection with this loan. If I request (1) I will be informed whether or not consumer reports were obtained, and (2) if reports were obtained, I will be informed of the names and addresses of the credit bureaus that furnished the reports. If you agree to make this loan to me, a consumer credit report may be requested or used in connection with renewals or extensions of any credit for which I have applied, reviewing the loan, taking collection action on the loan, or legitimate purposes associated with the loan. \\n\\t- OH RESIDENTS:The Ohio laws against discrimination require that all creditors make credit equally available to all credit worthy customers, and that credit reporting agencies maintain separate credit histories on each individual upon request. The Ohio Civil Rights Commission administers compliance with this law. \\n\\t- VT RESIDENTS:BY SIGNING OR ACKNOWLEDGING THIS APPLICATION, YOU CONSENT TO THE LENDER OBTAINING YOUR CONSUMER REPORT FROM A CONSUMER REPORTING AGENCY IN CONNECTION WITH THIS TRANSACTION OR EXTENSION OF CREDIT FOR THE PURPOSE OF REVIEWING YOUR LOAN ACCOUNT, FOR THE PURPOSE OF TAKING COLLECTION ACTION ON YOUR LOAN ACCOUNT, OR FOR OTHER LEGITIMATE PURPOSES ASSOCIATED WITH YOUR LOAN ACCOUNT. \\n\\t- WI RESIDENTS:No provision of any marital property agreement (pre-marital agreement), unilateral statement under Section 766.59 or court decree under Section 766.70 adversely affects the interest of the Lender unless the Lender, prior to the time that the loan is approved, is furnished with a copy of the agreement, statement, or decree or has actual knowledge of the adverse provision when the obligation to the Lender is incurred. If the loan for which I am applying is granted, my spouse will also receive notification that credit has been extended to me. \\n\\t- To help the government fight the funding of terrorism and money laundering activities, Federal law requires all financial institutions to obtain, verify, and record information that identifies every customer. When you apply for a loan, we will ask for your name, address, date of birth, and other information that will allow us to identify you. We may also ask to see your driver’s license or other identifying documents."
      }
    ]
    "meta": {
      "cursor": "123nknj"
    }
}
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/getRestV2Disclosures"
      }}>{`Get Disclosures`}</a>{` in the API documentation.`}</p>
    <p><strong parentName="p">{`Here's an example of how to render the disclosures from the sample payload above.`}</strong></p>
    <details>
  <summary>Disclosure Rendering Example</summary>
  <Image src={disclosureRendering} alt="Disclosure Rendering Example" placeholder="blur" mdxType="Image" />
    </details>
    <h3>{`3. Submit a Loan Application`}</h3>
    <p>{`Once a borrower has completed your form, making a `}<inlineCode parentName="p">{`POST`}</inlineCode>{` request to `}<inlineCode parentName="p">{`/v2/loans`}</inlineCode>{` will `}<strong parentName="p">{`send the application`}</strong>{` over to GoodLeap. `}<em parentName="p">{`note: for Home Improvement loans it is required that the submitting user email is a valid user's email in your organization. If you are submitting a Home Improvement or Roofing Category ID and do not provide this you will receive an error.`}</em></p>
    <blockquote>
      <p parentName="blockquote">{`Here’s an example of the payload to send us:`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "applicant": { <<insert applicant information>> },
    "coApplicant": { <<insert co-applicant information>> },
    "categoryId": "string",
    "submittingUser": { <<insert submitting user information>> },
    "offerId": "string",
    "amount": {
        "type": "USD",
        "value": 100.11
    },
    "channel": "string",
    "agent": "string",
    "applicantSignature": "string",
    "subjectProperty": {
        "street": "string",
        "street2": "string",
        "city": "string",
        "state": "AK",
        "zip": "string"
    },
    "referenceNumber": "string",
    "disclosureToken": "string",
    "enrollments": [
        "AUTOPAY"
    ]
}
`}</code></pre>
    <p>{`You should expect a response like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "id": "string",
    "referenceNumber": "string",
    "type": "SINGLE",
    "status": "APPROVED",
    "approvedLoanAmount": {
        "type": "USD",
        "value": 100.11
    },
    "requestedLoanAmount": {
        "type": "USD",
        "value": 100.11
    },
    "offerId": "string",
    "sentLoanDocuments": true,
    "stipulations": [ <<list of stipulations>> ]
}
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/postRestV2Loans"
      }}>{`Create a Loan`}</a>{` in the API documentation.`}</p>
    <h2>{`Managing a Loan Project`}</h2>
    <h3>{`1. Get a Loan by its ID or Reference Number`}</h3>
    <p><inlineCode parentName="p">{`GET /v2/loans/:id`}</inlineCode>{` - fetches the loan by ID`}</p>
    <p><inlineCode parentName="p">{`GET /v2/loans?referenceNumber=`}</inlineCode>{` - gets a loan by reference number, returns a collection.`}</p>
    <blockquote>
      <p parentName="blockquote">{`For more information, see `}<a parentName="p" {...{
          "href": "/reference#operation/getRestV2LoansId"
        }}>{`Get Loan by ID`}</a>{` in the API documentation.`}</p>
    </blockquote>
    <h3>{`2. Working with Cases`}</h3>
    <p><inlineCode parentName="p">{`GET /v2/loans/:id/cases`}</inlineCode>{` retrieves all open stipulations or conditions for a given loan ID.
`}<inlineCode parentName="p">{`GET /v2/loans/:id/cases/uploadurl`}</inlineCode>{` retrieves an upload url that will allow you to submit documentation to resolve the conditions or stipulations associated with a specific loan ID. Once you have retrieved the proper upload url you will then PUT the needed documentation as binary data at that URL for underwriter review.`}</p>
    <blockquote>
      <p parentName="blockquote">{`for more information, see `}<a parentName="p" {...{
          "href": "/reference#operation/getPosfinancingRestV2LoansIdCases"
        }}>{`GET Cases`}</a>{` in the API documentation`}</p>
    </blockquote>
    <h3>{`3. Confirm Loan Details`}</h3>
    <p><inlineCode parentName="p">{`POST /v2/loans/:id/details`}</inlineCode></p>
    <p><em parentName="p">{`Changes can be made to the loan by making a `}<inlineCode parentName="em">{`POST`}</inlineCode>{` request to `}<inlineCode parentName="em">{`/v2/loans/:id/details`}</inlineCode>{`.`}</em></p>
    <blockquote>
      <p parentName="blockquote">{`Here's an example of a payload to send us to confirm loan details:`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "offerId": "ee7ea1c7-5bad-422c-b567-ec40e1170e3d",
  "amount": {
    "type": "USD",
    "value": 100.11
  }
}
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/postRestV2LoansIdDetails"
      }}>{`Confirm Details`}</a>{` in the API documentation.`}</p>
    <h3>{`4. Submit a Change Order`}</h3>
    <p>{`If a loan’s enrollment, amount, or offers need updating, you can make a `}<inlineCode parentName="p">{`POST`}</inlineCode>{` request to `}<inlineCode parentName="p">{`/v2/loans/:id/changeOrder`}</inlineCode>{` to submit a change order.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Here's an example of a payload to send us to change the loan offer to offer ID, loan amount \\$40,000, and enroll in the autopay promotion:
note: when changing an offer please ensure that you provide the guid from the 'GET /offers' endpoint for the particular offer you'd like to change the loan to`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "changes": [
    {
      "type": "OFFER",
      "value": "ee7ea1c7-5bad-422c-b567-ec40e1170e3e"
    },
    {
      "type": "AMOUNT",
      "value": 40000.0
    },
    {
      "type": "ENROLLMENT",
      "value": [
        {
          "type": "ADD",
          "value": "AUTOPAY"
        }
      ]
    }
  ]
}
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/postRestV2LoansIdChangeorders"
      }}>{`Create a Change Order`}</a>{` in the API documentation.`}</p>
    <h3>{`5. Upload a Home Improvement Contract (HIC)`}</h3>
    <p><inlineCode parentName="p">{`GET /rest/v2/loans/:id/uploadurl`}</inlineCode></p>
    <p>{`Uploading a HIC is a two step process. First, make a a `}<inlineCode parentName="p">{`GET`}</inlineCode>{` request to `}<inlineCode parentName="p">{`/v2/loans/:id/uploadurl?supplemental=true|false&uploadType=HIC`}</inlineCode>{` to get an upload URL you can use to upload the Home Improvement Contract. Second upload the Home Improvement Contract to the url provided in the response body of `}<inlineCode parentName="p">{`GET /v2/loans/:id/uploadurl?supplemental=true|false&uploadType=HIC`}</inlineCode></p>
    <blockquote>
      <p parentName="blockquote">{`here is an example of the 'GET HIC Uploadurl'`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`GET /v2/loans/:id/uploadurl?supplemental=true|false&uploadType=HIC
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/getRestV2LoansIdUploadurls"
      }}>{`Get an Upload URL`}</a>{` in the API documentation.`}</p>
    <h3>{`6. Add Project Info`}</h3>
    <p><inlineCode parentName="p">{`GET /v2/categories/:id/components`}</inlineCode></p>
    <blockquote>
      <p parentName="blockquote"><em parentName="p">{`Prior to adding project info, get the list of valid manufacturers for the components of the category of the loan which have manufacturers.`}</em></p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "components": [
    {
      "type": "BATTERY",
      "manufacturers": ["SomeManufacturer", "SomeManufacturer2"]
    },
    {
      "type": "INVERTER",
      "manufacturers": ["SomeManufacturer", "SomeManufacturer2"]
    },
    {
      "type": "PANEL",
      "manufacturers": ["SomeManufacturer", "SomeManufacturer2"]
    }
  ]
}
`}</code></pre>
    <p><inlineCode parentName="p">{`PUT /v2/loans/:id/project`}</inlineCode></p>
    <blockquote>
      <p parentName="blockquote"><em parentName="p">{`You can add project information loan by making a `}<inlineCode parentName="em">{`PUT`}</inlineCode>{` request to `}<inlineCode parentName="em">{`/v2/loans/:id/project`}</inlineCode>{`.`}</em>{`
Here's an example of a payload to send us to add project info:`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "components": [
    {
      "type": "BATTERY",
      "manufacturer": "SomeManufacturer",
      "quantity": 5,
      "capacity": 40.3
    },
    {
      "type": "INVERTER",
      "manufacturer": "SomeManufacturer"
    },
    {
      "type": "PANEL",
      "manufacturer": "SomeManufacturer"
    },
    {
      "type": "SYSTEM_SIZE",
      "value": 12.035
    },
    {
      "type": "SYSTEM_EAP",
      "value": 4203
    }
  ]
}
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/putRestV2LoansIdProject"
      }}>{`Add Project Info`}</a>{` in the API documentation.`}</p>
    <h3>{`7. Change Milestones`}</h3>
    <p><inlineCode parentName="p">{`POST /v2/loans/:id/milestones`}</inlineCode></p>
    <blockquote>
      <p parentName="blockquote"><em parentName="p">{`Milestone changes can be made to the loan by making a `}<inlineCode parentName="em">{`POST`}</inlineCode>{` request to `}<inlineCode parentName="em">{`/v2/loans/:id/milestones`}</inlineCode>{`.`}</em>{`
Here's an example of a payload to send us to change milestones:`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "INSTALL_COMPLETE",
  "completedAt": "2020-09-03T19:42:03.050Z"
}
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/postRestV2LoansIdMilestones"
      }}>{`Transition a Milestone`}</a>{` in the API documentation.`}</p>
    <h2>{`Other Common Actions`}</h2>
    <h3>{`1. Calculate a borrower’s monthly payments`}</h3>
    <p><inlineCode parentName="p">{`GET /v2/payments`}</inlineCode></p>
    <p>{`The payments endpoint will allow you to pass an offer ID, paydown amount, promotion ID in order to get the estimated monthly payments for the chosen loan offer.`}</p>
    <blockquote>
      <p parentName="blockquote">{`For more information, see `}<a parentName="p" {...{
          "href": "/reference#operation/getRestV2Payments"
        }}>{`Get Payments`}</a>{` in the API documentation.`}</p>
    </blockquote>
    <h3>{`2. Cancel a loan`}</h3>
    <p><inlineCode parentName="p">{`POST /v2/loans/:id/cancellations`}</inlineCode></p>
    <blockquote>
      <p parentName="blockquote">{`For more information, see `}<a parentName="p" {...{
          "href": "/reference#operation/postRestV2LoansIdCancellations"
        }}>{`Cancel a Loan`}</a>{` in the API documentation.`}</p>
    </blockquote>
    <h3>{`3. Add/retrieve notes to a loan or case`}</h3>
    <p><inlineCode parentName="p">{`GET /v2/loans/:id/notes`}</inlineCode>{`
Will fetch the public notes associated with the loan ID passed. Optionally you can pass a case ID and retrieve notes associated with the case as well.`}</p>
    <p><inlineCode parentName="p">{`POST /v2/loans/:id/notes`}</inlineCode>{`
Allows you to add a note to the loan by passing in a body element in the 'POST' request. Optionally, can also pass in a valid user email and the note will have the users 'firstName' and 'lastName' associated with the note.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "body": "this is a note",
  "email": "originUser@testdomain.com"
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`For more information see `}<a parentName="p" {...{
          "href": "/reference#tag/Notes"
        }}>{`Notes`}</a>{` in the API documentation.`}</p>
    </blockquote>
    <h3>{`4. Share Application`}</h3>
    <p>{`By calling `}<inlineCode parentName="p">{`POST /v2/loans-share`}</inlineCode>{` you will be able to get a URL and email for the application so that it may be shared with customers to complete the loan information. It should be noted that sharing an application requires a submitting user who is already created in your organization either via the API or in Origin.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "offerId": "string",
  "state": "CA, UT, NY",
  "amount": {
    "type": "USD",
    "value": 100.11
  },
  "applicant": {
    "firstName": "string",
    "lastName": "string",
    "email": "string"
  },
  "sendEmail": true,
  "submittingUser": {
    "firstName": "string",
    "lastName": "string",
    "email": "string",
    "phone": "string"
  }
}
`}</code></pre>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/reference#operation/postPosfinancingRestV2Loansshare"
      }}>{`Loans-share`}</a>{` in the API documentation.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;